import { createApi } from "@reduxjs/toolkit/query/react";

import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { REQUEST_METHODS } from "@/constants/global-constants";
import { IActivity } from "@/interfaces/activity.interface";
import {
  ILeads,
  TEstimationData,
} from "@/interfaces/project-register.interface";
import { ExtendedDriveResponse } from "@/interfaces/project.interface";
import { TAllOptions } from "@/schemas/GlobalSchema";
import { TEOption } from "@/schemas/project-register.schema";
import { BaseWithAuth, getToken, getWorkspace } from "@/utils/helpers";

export const prApi = createApi({
  reducerPath: "prApi",
  baseQuery: BaseWithAuth(),
  tagTypes: [
    "leads",
    "single-lead",
    "drive",
    "options",
    "estimate",
    "single-estimate",
    "activity",
  ],
  endpoints: (builder) => ({
    getSingleLead: builder.query<ILeads, { id?: string }>({
      query: ({ id }) => ({
        url: `workspace/${getWorkspace()}/leads/${id ? id : ""}`,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      providesTags: ["single-lead"],
      transformResponse: (response: APIResponse<ILeads>) => response?.data,
    }),
    // get single and all with filters
    getLeads: builder.query<
      GeneralResponse<ILeads[]> & {
        meta: {
          referenceNumber: string;
        };
      },
      { filters?: string }
    >({
      query: ({ filters }) => ({
        url: `workspace/${getWorkspace()}/leads${filters ? filters : ""}`,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      providesTags: ["leads", "activity"],
    }),

    alterLeads: builder.mutation<
      object,
      {
        id?: string;
        data?: object;
        method: REQUEST_METHODS;
      }
    >({
      query: ({ data, id, method }) => ({
        url: `workspace/${getWorkspace()}/leads/${id ? id : ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => {
        // Make sure these strings match exactly with your tag definitions
        const tags: ("leads" | "single-lead" | "activity")[] = [];
        tags.push("activity");
        if (id) {
          tags.push("single-lead");
        } else {
          tags.push("leads");
        }

        return tags;
      },
    }),

    getOption: builder.query<TAllOptions[], { filters: string }>({
      query: ({ filters }) => ({
        url: `workspace/${getWorkspace()}/leads/options?${filters ? `for=${filters}` : ""}`,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      providesTags: ["options"],
      transformResponse: (response: APIResponse<TAllOptions[]>) =>
        response?.data,
    }),

    alterOptions: builder.mutation<
      object,
      {
        id?: string;
        data?: object;
        method: REQUEST_METHODS;
      }
    >({
      query: ({ data, id, method }) => ({
        url: `workspace/${getWorkspace()}/leads/options/${id ? id : ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["options", "activity"],
    }),

    updateLeadsStatus: builder.mutation<
      object,
      {
        id: string;
        data: object;
      }
    >({
      query: ({ id, data }) => ({
        url: `workspace/${getWorkspace()}/leads/${id}/status`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["leads", "drive", "activity"],
    }),

    // Drive
    getDocument: builder.query<
      ExtendedDriveResponse,
      { id: string; filters?: string }
    >({
      query: ({ id, filters }) => ({
        url: `workspace/${getWorkspace()}/leads/${id}/drive/${filters ? filters : `driveType/${driveTypes.PROJECT_REGISTER}`}`,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      providesTags: ["drive"],
    }),

    // mutate Drive
    alterLeadsDoc: builder.mutation<
      object,
      {
        leadsId?: string;
        data?: object;
        method: string;
        params?: string;
        timeout?: number;
      }
    >({
      query: ({ leadsId, data, method, params, timeout }) => ({
        url: `workspace/${getWorkspace()}/leads/${leadsId}/document/${params ? params : ""}`,
        method: method.toUpperCase(),
        body: data,
        timeout,
      }),
      invalidatesTags: ["drive", "activity"],
    }),

    alterLeadsDrive: builder.mutation<
      object,
      {
        leadsId?: string;
        data?: object;
        method: string;
        params?: string;
        timeout?: number;
      }
    >({
      query: ({ leadsId, data, method, params, timeout }) => ({
        url: `workspace/${getWorkspace()}/leads/${leadsId}/${params ? params : ""}`,
        method: method.toUpperCase(),
        body: data,
        timeout,
      }),
      invalidatesTags: ["drive", "activity"],
    }),

    // From here only add the leads-estimation query
    getAllEstimates: builder.query<GeneralResponse<TEstimationData[]>, string>({
      query: (filters) =>
        `workspace/${getWorkspace()}/estimates${filters ? filters : ""}`,
      providesTags: ["estimate"],
    }),

    getEstimates: builder.query<
      GeneralResponse<TEstimationData[]>,
      { id: string; filters?: string }
    >({
      query: ({ id, filters }) =>
        `workspace/${getWorkspace()}/leads/${id}/estimate/${filters ? filters : ""}`,
      providesTags: ["estimate"],
    }),
    getSingleEstimate: builder.query<
      GeneralSingleResponse<TEstimationData>,
      { id: string; estimateId: string }
    >({
      query: ({ id, estimateId }) =>
        `workspace/${getWorkspace()}/leads/${id}/estimate/${estimateId}`,
      providesTags: ["single-estimate"],
    }),

    getActivity: builder.query<IActivity[], string>({
      query: (id) => `workspace/${getWorkspace()}/leads/${id}/activity`,
      providesTags: ["leads", "estimate"],
      transformResponse: (response: APIResponse<IActivity[]>) => response?.data,
    }),

    mutateEstimate: builder.mutation<
      object,
      {
        id: string;
        data: object;
        estimateId?: string;
      }
    >({
      query: ({ id, data, estimateId }) => ({
        url: `workspace/${getWorkspace()}/leads/${id}/estimate/${estimateId ? estimateId : ""}`,
        method: estimateId ? "PUT" : "POST",
        body: data,
      }),

      invalidatesTags: (_result, _error, { estimateId }) => {
        const tags: ("estimate" | "single-estimate" | "activity")[] = [];
        tags.push("activity");

        if (estimateId) {
          tags.push("single-estimate");
        } else {
          tags.push("estimate");
        }

        return tags;
      },
    }),

    setEstimateStatus: builder.mutation<
      object,
      {
        id: string;
        estimateId: string;
        type: string;
      }
    >({
      query: ({ id, estimateId, type }) => ({
        url: `workspace/${getWorkspace()}/leads/${id}/estimate/${estimateId}/${type}`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { type }) => {
        // Make sure these strings match exactly with your tag definitions
        const tags: ("estimate" | "leads" | "activity")[] = ["estimate"];
        tags.push("activity");
        if (type === "final" || type === "undo") {
          tags.push("leads");
        }

        return tags;
      },
    }),

    // options like label and submission method
    getEstimateOptions: builder.query<
      TEOption[],
      { id: string; OptionFor?: string }
    >({
      query: ({ id, OptionFor }) =>
        `workspace/${getWorkspace()}/leads/${id}/estimate/options?${OptionFor ? `for=${OptionFor}` : ""}`,
      providesTags: ["options"],
      transformResponse: (response: APIResponse<TEOption[]>) => response?.data,
    }),

    mutateEstimateOptions: builder.mutation<
      object,
      {
        id: string;
        optionId?: string;
        method: Omit<REQUEST_METHODS, "PATCH">;
        data?: object;
      }
    >({
      query: ({ id, optionId, method, data }) => ({
        url: `workspace/${getWorkspace()}/leads/${id}/estimate/options/${optionId ? optionId : ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["options", "activity"],
    }),

    mutateEstimateReview: builder.mutation<
      object,
      {
        leadsId: string;
        estimateId: string;
        data: object;
      }
    >({
      query: ({ leadsId, estimateId, data }) => ({
        url: `workspace/${getWorkspace()}/leads/${leadsId}/estimate/${estimateId}/review`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["leads", "single-estimate", "activity"],
    }),

    uploadEstimateReviewImage: builder.mutation<
      object,
      {
        leadsId: string;
        data: object;
      }
    >({
      query: ({ leadsId, data }) => ({
        url: `workspace/${getWorkspace()}/leads/${leadsId}/estimate/image`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["activity"],
    }),
  }),
});

export const {
  useGetSingleLeadQuery,
  useGetLeadsQuery,
  useAlterLeadsMutation,
  useGetOptionQuery,
  useAlterOptionsMutation,
  useUpdateLeadsStatusMutation,
  // Drive
  useGetDocumentQuery,
  useAlterLeadsDocMutation,
  useAlterLeadsDriveMutation,

  // Estimation
  useGetAllEstimatesQuery,
  useGetEstimatesQuery,
  useGetSingleEstimateQuery,
  useGetActivityQuery,
  useMutateEstimateMutation,
  useSetEstimateStatusMutation,
  useGetEstimateOptionsQuery,
  useMutateEstimateOptionsMutation,
  useMutateEstimateReviewMutation,
  useUploadEstimateReviewImageMutation,
} = prApi;
